import React, { useEffect, useRef } from "react";
import { View, Image, StyleSheet, Animated, Easing } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import CustomText from "../../../common/general/CustomText/CustomText";
import { colors, spacing, radii, shadowStyles } from "../../../../styles/design-system";

export interface GeneratedImageCardProps {
  status: "loading" | "completed" | "failed";
  imageUrl?: string | null;
  errorMessage?: string | null;
}

// 2× the max container width so the slow pan has room to slide
const BASE_GRADIENT_WIDTH = 960;

function AIImageGeneratingPlaceholder() {
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  const baseAnim = useRef(new Animated.Value(0)).current;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Recursive restart is more reliable than Animated.loop + useNativeDriver on web
    const runShimmer = () => {
      shimmerAnim.setValue(0);
      Animated.timing(shimmerAnim, {
        toValue: 1,
        duration: 1600,
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished && mountedRef.current) runShimmer();
      });
    };

    // Matches animate-gradient-slow: 6 s total, ease in-out, back and forth
    const baseLoop = Animated.loop(
      Animated.sequence([
        Animated.timing(baseAnim, {
          toValue: 1,
          duration: 3000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(baseAnim, {
          toValue: 0,
          duration: 3000,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ])
    );

    runShimmer();
    baseLoop.start();

    return () => {
      mountedRef.current = false;
      shimmerAnim.stopAnimation();
      baseLoop.stop();
    };
  }, [shimmerAnim, baseAnim]);

  // Slides the oversized gradient left by half its extra width (480 px)
  const baseTranslateX = baseAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, -480],
  });

  // Sweeps fully off both edges for any container up to maxWidth
  const shimmerTranslateX = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [-600, 600],
  });

  return (
    <View style={styles.placeholder}>
      {/* Base gradient — oversized, pans slowly left/right */}
      <Animated.View style={[styles.baseGradientContainer, { transform: [{ translateX: baseTranslateX }] }]}>
        <LinearGradient
          colors={[colors.brand[50], "#f5f0fe", colors.brand[100], colors.brand[50]]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{ flex: 1 }}
        />
      </Animated.View>
      {/* Shimmer stripe — diagonal highlight beam, 110 ° */}
      <Animated.View style={[styles.shimmer, { transform: [{ translateX: shimmerTranslateX }, { skewX: "-20deg" }] }]}>
        <LinearGradient
          colors={[
            "rgba(255,255,255,0)",
            "rgba(255,255,255,0.06)",
            "rgba(255,255,255,0.14)",
            "rgba(255,255,255,0.06)",
            "rgba(255,255,255,0)",
          ]}
          locations={[0, 0.3, 0.5, 0.7, 1]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={StyleSheet.absoluteFill}
        />
      </Animated.View>
    </View>
  );
}

export const GeneratedImageCard = ({ status, imageUrl, errorMessage }: GeneratedImageCardProps) => {
  if (status === "loading") {
    return (
      <View style={styles.loadingContainer}>
        <CustomText useTranslationText={false} text="Generating image..." size="s" style={styles.loadingText} />
        <AIImageGeneratingPlaceholder />
      </View>
    );
  }

  if (status === "failed") {
    return (
      <View style={styles.failedContainer}>
        <CustomText
          useTranslationText={false}
          text={errorMessage ?? "Image generation failed, please try again later."}
          size="s"
          style={styles.errorText}
        />
      </View>
    );
  }

  if (status === "completed" && imageUrl) {
    return (
      <View style={styles.imageContainer}>
        <Image source={{ uri: imageUrl }} style={styles.image} resizeMode="contain" />
      </View>
    );
  }

  return null;
};

const styles = StyleSheet.create({
  loadingContainer: {
    marginTop: spacing.xs,
    gap: spacing.xs,
  },
  placeholder: {
    width: "100%",
    maxWidth: 480,
    aspectRatio: 1,
    borderRadius: radii.xl,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "rgba(204, 215, 255, 0.6)",
  },
  baseGradientContainer: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    width: BASE_GRADIENT_WIDTH,
  },
  shimmer: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    width: 200,
  },
  loadingText: {
    color: colors.grey[500],
  },
  failedContainer: {
    paddingVertical: spacing.xs,
  },
  errorText: {
    color: colors.error[500],
  },
  imageContainer: {
    borderRadius: radii.xl,
    overflow: "hidden",
    marginTop: spacing.xs,
    maxWidth: 480,
    ...shadowStyles.sm,
  },
  image: {
    width: "100%",
    aspectRatio: 1,
    borderRadius: radii.xl,
  },
});
